import axios from "axios";
import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import PrimaryButton from "../../../Components/shared/PrimaryButton";
import { commentListUrl, addCommentUrl } from "../../../Config/config";
import { IoPersonCircleSharp } from 'react-icons/io5';
import Loader from "../../../Components/Loader";
import moment from 'moment';
import { UserContext } from "../../../Contexts/UserContext";
import logo from "../../../logo/logo192.png";
import { clickableParagraphHavingUrlsAndLinebreak } from "../../../Components/CommonUtils";

const ProfileHighlights = ({ ticketId, author }) => {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const { getToken } = useContext(UserContext);

  /**
   * The function `getComments` makes a POST request to retrieve comments for a ticket using the
   * provided ticket ID and author, and updates the state with the retrieved comments.
   */
  const getComments = () => {
    setIsLoading(true);
    axios.post(commentListUrl, {
      "ticket_id": ticketId,
      "author": author ?? "loke2@prano.com",
    }, {
      headers: {
        "Authorization": getToken()
      }
    }).then(response => {
      setIsLoading(false);
      if (response.status === 200) {
        setComments(response.data?.comments ?? [])
      }
    })
  }

  useEffect(() => {
    if (ticketId != null && author != null) {
      getComments();
    }
  }, [ticketId, author])

  /**
   * The function `onCommentChange` updates the state variable `commentText` with the value of the input
   * field.
   */
  const onCommentChange = (e) => {
    setCommentText(e.target.value)
  }

  /**
   * The `onCommentPost` function is used to post a comment on a ticket and update the comments list.
   */
  const onCommentPost = () => {
    if (commentText.trim() === "") return;
    
    setIsCommenting(true);
    setCommentText("");
    axios.post(addCommentUrl, {
      "ticket_id": ticketId,
      "author": author,
      "comment": commentText,
    }, {
      headers: {
        "Authorization": getToken()
      }
    }).then(response => {
      setIsCommenting(false);
      if (response.status === 200) {
        getComments();
        toast.success(response.data?.message)
      }
    })
  }

  return (
    <div className="bg-white rounded-md shadow-sm w-[80%]  ml-12">
      {/* Comment list at the top */}
      {isLoading ? (
        <div className="h-32 flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="divide-y">
          {comments.map((comment, index) => (
            <div key={index} className="flex p-3">
              {comment?.author?.includes("@") ? (
                <IoPersonCircleSharp className="mt-1 flex-shrink-0 text-gray-600" size={36} />
              ) : (
                <img src={logo} alt="Logo" className="mt-1 w-8 h-8 rounded-full flex-shrink-0" />
              )}
              <div className="ml-3">
                <div className="flex items-center">
                  <span className={comment?.author?.includes("@") ? "text-base font-medium text-gray-700" : "text-base font-medium text-amber-600"}>
                    {comment?.author?.includes("@") ? "You" : comment?.author ?? "--"}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    {moment(comment?.created_at).format("DD MMM yyyy hh:mm A")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-700">
                  {clickableParagraphHavingUrlsAndLinebreak(comment?.comment || '')}
                </p>
              </div>
            </div>
          ))}
          {comments.length === 0 && !isLoading && (
            <div className="p-4 text-center text-gray-500">
              No comments yet
            </div>
          )}
        </div>
      )}
      
      {/* Dividing line */}
      <hr className="border-t border-gray-200" />
      
      {/* Comment input area at the bottom */}
      <div className="p-4 ml-10">
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md text-sm text-gray-600 bg-gray-50 focus:outline-none resize-none"
          rows="4"
          value={commentText}
          onChange={onCommentChange}
          placeholder="Type...."
        ></textarea>
        <div className="flex justify-end mt-2">
          <button 
            className="px-4 py-2 bg-[#bc8b20] text-white rounded-md font-medium text-sm disabled:opacity-50"
            disabled={isCommenting || commentText.trim() === ""}
            onClick={onCommentPost}
          >
            {isCommenting ? "Posting..." : "Post Comments"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHighlights;